/**
 * Contexto de permisos dinámicos.
 *
 * Flujo al autenticarse:
 * 1. Decodifica el JWT access_token para obtener email y roles
 * 2. Carga el catálogo de permisos de la aplicación
 * 3. Busca el usuario en la BD por email
 * 4. Carga los permisos del usuario (copiados de su rol al crearlo)
 * 5. Superadmin se evalúa con sus permisos reales, igual que cualquier otro rol;
 *    solo las tres rutas de gestión de permisos quedan gateadas por el rol
 *    (ver "__SUPERADMIN_ONLY__" en entity-mapping.ts), no por sus grants
 */
import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  type ReactNode,
} from "react";
import { useAuth } from "react-oidc-context";
import type { PermissionAction, UserRole, AuthPermissionDto } from "@/types/permissions";
import {
  getUserPermissions,
  getUserByEmail,
  getPermissionsCatalog,
  getRoleTemplate,
  extractRows,
} from "@/services/permissions-api";
import { canAccessRoute } from "@/lib/permissions/entity-mapping";

// ─── Tipos ──────────────────────────────────────────────────────────────────

interface PermissionsState {
  grants: Set<string>;
  userRole: UserRole | null;
  userId: number | null;
  userEntityId: number | null;
  userEmail: string | null;
  isSuperadmin: boolean;
  loading: boolean;
  error: string | null;
  catalog: AuthPermissionDto[];
}

interface PermissionsContextValue extends PermissionsState {
  hasPermission: (entity: string, action: PermissionAction) => boolean;
  canAccess: (href: string) => boolean;
  reload: () => Promise<void>;
}

const PermissionsContext = createContext<PermissionsContextValue | undefined>(undefined);

export function usePermissions() {
  const ctx = useContext(PermissionsContext);
  if (!ctx) throw new Error("usePermissions must be used within PermissionsProvider");
  return ctx;
}

// ─── Helpers (exported for testing) ──────────────────────────────────────────

export function permKey(entity: string, action: string): string {
  return `${entity}:${action}`;
}

/** Decodifica el payload de un JWT sin verificar firma */
export function decodeJwt(token: string): Record<string, any> {
  try {
    const payload = token.split(".")[1];
    const decoded = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    return JSON.parse(decoded);
  } catch {
    return {};
  }
}

/** Determina el rol Nemesis a partir de los roles del JWT */
export function resolveNemesisRole(jwtRoles: string | string[] | unknown): UserRole {
  const raw = Array.isArray(jwtRoles) ? jwtRoles : jwtRoles ? [jwtRoles] : [];
  const roles = raw.filter((r): r is string => typeof r === "string");
  const lower = roles.map((r) => r.toLowerCase());
  if (lower.includes("superadmin")) return "SUPERADMIN";
  if (lower.includes("admin") || lower.includes("administrador")) return "ADMINISTRADOR";
  if (lower.includes("editor")) return "EDITOR";
  return "USUARIO";
}

// Entidades con permisos fijos para todos los usuarios (no editables)
const ALWAYS_GRANTED_ENTITIES = ["SavedViews"];

// ─── Provider ───────────────────────────────────────────────────────────────

export function PermissionsProvider({ children }: { children: ReactNode }) {
  const auth = useAuth();
  const [state, setState] = useState<PermissionsState>({
    grants: new Set<string>(),
    userRole: null,
    userId: null,
    userEntityId: null,
    userEmail: null,
    isSuperadmin: false,
    loading: true,
    error: null,
    catalog: [],
  });

  // Cache para reutilizar en reloads cuando getUserByEmail falla (ej: perdió Users.READ)
  const cachedUserIdRef = useRef<number | null>(null);
  const cachedUserEntityIdRef = useRef<number | null>(null);
  const cachedDbRoleRef = useRef<UserRole | null>(null);

  const loadPermissions = useCallback(async () => {
    if (!auth.isAuthenticated || !auth.user) {
      setState((s) => ({ ...s, loading: false }));
      return;
    }

    const accessToken = auth.user.access_token;
    const claims = accessToken ? decodeJwt(accessToken) : {};
    const email = (claims.email ?? claims.client_email ?? auth.user.profile?.email ?? "") as string;
    const jwtRoles = [
      ...(Array.isArray(claims.roles) ? claims.roles : claims.roles ? [claims.roles] : []),
      ...(claims.role && !claims.roles ? [claims.role] : []),
    ] as string[];
    const nemesisRole = resolveNemesisRole(jwtRoles);
    const isSuperadmin = nemesisRole === "SUPERADMIN";


    setState((s) => ({ ...s, loading: true, error: null }));

    const grants = new Set<string>();
    let userId: number | null = null;
    let userEntityId: number | null = null;
    let dbRole: UserRole = nemesisRole;
    let catalog: AuthPermissionDto[] = [];

    try {
      // 1. Cargar catálogo de permisos
      const catalogRes = await getPermissionsCatalog();

      const catRows = extractRows<AuthPermissionDto>(catalogRes.data);
      if (catalogRes.success && catRows) {
        catalog = catRows;
      }

      // 2. Buscar el usuario en la BD por email
      let useRoleTemplateFallback = false;
      if (email) {
        const dbUser = await getUserByEmail(email);
        if (dbUser) {
          userId = dbUser.id;
          userEntityId = dbUser.entityId ?? null;
          dbRole = (dbUser.role?.toUpperCase() as UserRole) || nemesisRole;
          // Cachear para futuros reloads
          cachedUserIdRef.current = userId;
          cachedUserEntityIdRef.current = userEntityId;
          cachedDbRoleRef.current = dbRole;
        } else if (cachedUserIdRef.current) {
          // getUserByEmail falló (probablemente perdió Users.READ), usar cache
          console.warn("[Permissions] getUserByEmail falló, usando userId cacheado:", cachedUserIdRef.current);
          userId = cachedUserIdRef.current;
          userEntityId = cachedUserEntityIdRef.current;
          dbRole = cachedDbRoleRef.current || nemesisRole;
        } else {
          // Sin userId ni cache: usaremos role template como fallback
          console.warn(`[Permissions] No se pudo obtener userId para "${email}". Se usará role template de "${nemesisRole}" como fallback.`);
          useRoleTemplateFallback = true;
        }
      }

      // 2b. Recalcular isSuperadmin con el rol de la BD (el JWT puede no tenerlo correctamente)
      const finalIsSuperadmin = isSuperadmin || dbRole === "SUPERADMIN";

      // 3. Cargar permisos del usuario (los permisos del rol ya fueron copiados al usuario al crearlo)
      if (userId) {
        const permsRes = await getUserPermissions(userId);
        const userPermRows = extractRows<any>(permsRes.data);
        if (permsRes.success && userPermRows) {
          for (const p of userPermRows) {
            if (p.isActive && p.permission) {
              grants.add(permKey(p.permission.entity, p.permission.action));
            }
          }
        }
      }

      // 3b. Fallback: si no tenemos userId, cargar permisos del role template
      if (useRoleTemplateFallback && catalog.length > 0) {
        const tmplRes = await getRoleTemplate(nemesisRole);
        if (tmplRes.success && tmplRes.data) {
          const templateData = tmplRes.data as any;
          const permissionIds: number[] = templateData.permissionIds ?? [];
          // Mapear IDs del template a entity:action usando el catálogo
          const catalogById = new Map(catalog.map((p) => [p.id, p]));
          for (const pid of permissionIds) {
            const perm = catalogById.get(pid);
            if (perm) {
              grants.add(permKey(perm.entity, perm.action));
            }
          }
        } else {
          console.warn("[Permissions] No se pudo cargar role template:", tmplRes.message);
        }
      }

      setState({
        grants,
        userRole: dbRole,
        userId,
        userEntityId,
        userEmail: email,
        isSuperadmin: finalIsSuperadmin,
        loading: false,
        error: null,
        catalog,
      });
    } catch (err: any) {
      console.error("[Permissions] Error loading:", err);
      // Si ya teníamos grants previos, mantenerlos en lugar de resetear a 0
      setState((prev) => ({
        ...prev,
        loading: false,
        error: err?.message ?? "Error cargando permisos",
      }));
    }
  }, [auth.isAuthenticated, auth.user]);

  useEffect(() => {
    loadPermissions();
  }, [loadPermissions]);

  const hasPermission = useCallback(
    (entity: string, action: PermissionAction): boolean => {
      if (state.loading) return true;
      if (ALWAYS_GRANTED_ENTITIES.includes(entity)) return true;
      return state.grants.has(permKey(entity, action));
    },
    [state.grants, state.loading],
  );

  const readEntities = useMemo(() => {
    const set = new Set<string>();
    for (const key of state.grants) {
      const [entity, action] = key.split(":");
      if (action === "READ") set.add(entity);
    }
    return set;
  }, [state.grants]);

  const canAccess = useCallback(
    (href: string): boolean => {
      if (state.loading) return true;
      return canAccessRoute(href, readEntities, state.isSuperadmin);
    },
    [readEntities, state.isSuperadmin, state.loading],
  );

  const value = useMemo<PermissionsContextValue>(
    () => ({
      ...state,
      hasPermission,
      canAccess,
      reload: loadPermissions,
    }),
    [state, hasPermission, canAccess, loadPermissions],
  );

  return (
    <PermissionsContext.Provider value={value}>
      {children}
    </PermissionsContext.Provider>
  );
}
